import React, { useState, useMemo, useRef } from 'react';
import { Check, Send, RotateCcw } from 'lucide-react';
import { ActionPill, EmptyState } from '../../../ui';
import { useInbox } from '../InboxContext';
import { ThreadCard, groupEmailsIntoThreads } from '../shared/ThreadCard';

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

// ── Mini calendar for reschedule popover ─────────────────────────────────────
const MC_DAYS = ['S','M','T','W','T','F','S'];
function MiniCal({ value, onChange }) {
  const todayObj = new Date();
  const init = () => {
    if (value) { const [y, m] = value.split('-').map(Number); return new Date(y, m - 1, 1); }
    return new Date(todayObj.getFullYear(), todayObj.getMonth(), 1);
  };
  const [view, setView] = useState(init);
  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const dim = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  const toStr = (d) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <button type="button" onClick={() => setView(new Date(year, month - 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--navy-deep)' }}>{'\u2039'}</button>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-deep)' }}>{view.toLocaleString('en-US', { month: 'long' })} {year}</span>
        <button type="button" onClick={() => setView(new Date(year, month + 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--navy-deep)' }}>{'\u203A'}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1 }}>
        {MC_DAYS.map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', padding: '2px 0' }}>{d}</div>)}
        {cells.map((d, i) => d == null
          ? <div key={`e${i}`} />
          : <button key={d} type="button" onClick={() => onChange(toStr(d))} style={{
              textAlign: 'center', fontSize: 11, padding: '4px 0', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: value === toStr(d) ? 'var(--aqua)' : 'transparent',
              color: value === toStr(d) ? 'white' : 'var(--navy-deep)', fontWeight: value === toStr(d) ? 700 : 400,
            }}>{d}</button>
        )}
      </div>
    </div>
  );
}

function QueueThreadActions({ thread }) {
  const { actions } = useInbox();
  const [showReschedule, setShowReschedule] = useState(false);
  const [reschedDate, setReschedDate] = useState(thread.latestEmail.scheduledDate || '');
  const reschedRef = useRef(null);
  const ids = thread.emails.map((e) => e.id);

  const completeAll = () => ids.forEach((id) => actions.completeEmail(id));

  const assignThread = () => {
    const e = thread.latestEmail;
    const subject = encodeURIComponent(`Action Required: ${thread.subject}`);
    const body = encodeURIComponent(
      `Please review and action the following thread (${thread.emails.length} messages):\n\n${thread.subject}\nDomain: ${e.primaryDomain}\nParticipants: ${[...new Set(thread.emails.map((m) => m.from.name))].join(', ')}\n\n${e.preview || ''}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const confirmReschedule = () => {
    if (!reschedDate) return;
    ids.forEach((id) => actions.scheduleEmail(id, reschedDate));
    setShowReschedule(false);
  };

  return (
    <div>
      <ActionPill>
        <ActionPill.Button tip="Complete" color="var(--green)" onClick={completeAll}>
          <Check size={12} />
        </ActionPill.Button>
        <ActionPill.Button tip="Assign" color="var(--royal)" onClick={assignThread}>
          <Send size={12} />
        </ActionPill.Button>
        <span ref={reschedRef}>
          <ActionPill.Button tip="Reschedule" color="var(--text-dark-secondary)" onClick={() => setShowReschedule((v) => !v)}>
            <RotateCcw size={12} />
          </ActionPill.Button>
        </span>
      </ActionPill>

      {showReschedule && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setShowReschedule(false)} />
          <div style={{
            position: 'fixed', zIndex: 1050,
            top: Math.min((reschedRef.current?.getBoundingClientRect().bottom ?? 0) + 4, window.innerHeight - 320),
            left: Math.min((reschedRef.current?.getBoundingClientRect().left ?? 0), window.innerWidth - 260),
            background: 'var(--white)', borderRadius: 9, padding: 10,
            boxShadow: '0 4px 18px rgba(0,0,0,0.16)', border: '1px solid var(--surface)', minWidth: 230,
          }}>
            <MiniCal value={reschedDate} onChange={setReschedDate} />
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button type="button" onClick={confirmReschedule}
                style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
                  background: reschedDate ? 'var(--aqua)' : 'var(--surface)',
                  color: reschedDate ? 'white' : 'var(--text-muted)', cursor: reschedDate ? 'pointer' : 'default' }}>Confirm</button>
              <button type="button" onClick={() => setShowReschedule(false)}
                style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: '1px solid var(--surface)',
                  background: 'var(--white)', color: 'var(--text-dark-secondary)', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PriorityQueueView() {
  const { emails } = useInbox();
  const [filter, setFilter] = useState(null);

  const queued = useMemo(
    () => emails.filter((e) => e.queueStatus === 'queued' || e.queueStatus === 'scheduled'),
    [emails],
  );

  const threads = useMemo(
    () => groupEmailsIntoThreads(queued).sort(
      (a, b) => (PRIORITY_ORDER[a.highestPriority] ?? 4) - (PRIORITY_ORDER[b.highestPriority] ?? 4),
    ),
    [queued],
  );

  const highCount = threads.filter((t) => t.highestPriority === 'high' || t.highestPriority === 'critical').length;
  const medCount  = threads.filter((t) => t.highestPriority === 'medium').length;
  const lowCount  = threads.filter((t) => t.highestPriority === 'low').length;

  const displayed = filter
    ? threads.filter((t) => t.highestPriority === filter || (filter === 'high' && t.highestPriority === 'critical'))
    : threads;

  const FILTERS = [
    { key: null,     label: 'TOTAL',  count: threads.length, color: 'var(--text-muted)' },
    { key: 'high',   label: 'HIGH',   count: highCount,      color: 'var(--red-critical)' },
    { key: 'medium', label: 'MEDIUM', count: medCount,       color: 'var(--yellow)' },
    { key: 'low',    label: 'LOW',    count: lowCount,       color: 'var(--green)' },
  ];

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', padding: 8, gap: 8 }}>
      {/* Filter bar */}
      <div className="flex shrink-0 rounded-lg border border-nn-border overflow-hidden">
        {FILTERS.map(({ key, label, count, color }, idx) => {
          const active = filter === key;
          return (
            <button
              key={String(key)}
              type="button"
              onClick={() => setFilter(active ? null : key)}
              className="flex-1 flex items-center gap-1.5 px-3 py-1.5 border-none cursor-pointer transition-colors"
              style={{
                background: active ? 'var(--surface-warm)' : 'var(--white)',
                borderLeft: idx > 0 ? `2px solid ${color}` : undefined,
              }}
            >
              <span className="text-[18px] font-extrabold leading-none" style={{ color }}>{count}</span>
              <span className="text-[10px] font-semibold" style={{ color: active ? color : 'var(--text-dark-secondary)' }}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Queue list — now shows ThreadCards */}
      <div className="flex-1 overflow-y-auto space-y-1.5">
        {displayed.length === 0 ? (
          <EmptyState title="No queued items" body="Queue emails from the inbox to see them here." size="sm" />
        ) : (
          displayed.map((thread) => (
            <ThreadCard
              key={thread.threadId}
              thread={thread}
              variant="queue"
              actions={<QueueThreadActions thread={thread} />}
            />
          ))
        )}
      </div>
    </div>
  );
}

export { PriorityQueueView };
